import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Save, Trash2, Upload, Images, X, Monitor, Smartphone, GripVertical } from "lucide-react";
import { fetchSiteContent, updateSiteContent, uploadHeroImage, fetchProjects, mediaUrl, formatApiError } from "../../lib/api";
import { RichTextEditor } from "../../components/admin/RichTextEditor";

const HERO_POSITIONS = [
  "top left", "top", "top right",
  "left", "center", "right",
  "bottom left", "bottom", "bottom right",
];

const Field = ({ label, value, onChange, textarea, testid }) => (
  <div>
    <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] resize-y" />
    ) : (
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
    )}
  </div>
);

const ImageBlock = ({ prefix, url, uploading, fileRef, onUpload, onPick, onClear }) => (
  <>
    <div className="w-full max-w-[420px] min-h-[120px] bg-[#141312] overflow-hidden border border-[var(--vsl-border)] flex items-center justify-center">
      {url ? (
        <img src={mediaUrl(url)} alt="" data-testid={`${prefix}-image-preview`} className="max-h-[260px] w-auto max-w-full object-contain" />
      ) : (
        <span className="font-mono text-[10px] text-[var(--vsl-muted)] uppercase tracking-wider">No image</span>
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${prefix}-upload-btn`}
        className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors disabled:opacity-50">
        <Upload size={13} /> {uploading ? "Uploading…" : "Upload image"}
      </button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid={`${prefix}-file-input`} />
      <button onClick={onPick} data-testid={`${prefix}-pick-btn`}
        className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors">
        <Images size={13} /> Pick from projects
      </button>
      {url && (
        <button onClick={onClear} data-testid={`${prefix}-image-clear`}
          className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 text-[var(--vsl-muted)] hover:text-red-400 hover:border-red-400 transition-colors">
          <Trash2 size={13} /> Remove
        </button>
      )}
    </div>
  </>
);

export default function SiteTextPanel() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editLang, setEditLang] = useState("en");
  const [projects, setProjects] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingSingle, setUploadingSingle] = useState(null);
  const [heroDragIdx, setHeroDragIdx] = useState(null);
  const heroFileRef = useRef(null);
  const aboutFileRef = useRef(null);
  const contactsFileRef = useRef(null);
  const heroDrag = useRef(null);
  const sfx = editLang === "pt" ? "_pt" : "";

  useEffect(() => {
    fetchSiteContent().then((data) => {
      if (!(data.hero_images && data.hero_images.length) && data.hero_image) {
        data.hero_images = [{ url: data.hero_image, position: data.hero_position || "center" }];
      }
      if (!data.hero_images) data.hero_images = [];
      if (data.hero_interval == null) data.hero_interval = 5;
      setC(data);
    });
    fetchProjects().then(setProjects).catch(() => {});
  }, []);

  if (!c) return <p className="font-mono text-xs text-[var(--vsl-muted)]">Loading…</p>;

  const set = (base, v) => setC((prev) => ({ ...prev, [base + sfx]: v }));
  const g = (base) => c[base + sfx];

  const heroImages = c.hero_images || [];

  const persistSingleImage = async (field, url) => {
    let dims = { [`${field}_w`]: 0, [`${field}_h`]: 0 };
    if (url) {
      dims = await new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve({ [`${field}_w`]: img.naturalWidth, [`${field}_h`]: img.naturalHeight });
        img.onerror = () => resolve({ [`${field}_w`]: 0, [`${field}_h`]: 0 });
        img.src = mediaUrl(url);
      });
    }
    setC((prev) => ({ ...prev, [field]: url, ...dims }));
    try { await updateSiteContent({ [field]: url, ...dims }); toast.success(url ? "Image updated" : "Image removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const onSingleUpload = async (field, e, fileRef) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploadingSingle(field);
    try {
      const res = await uploadHeroImage(file);
      await persistSingleImage(field, res.url);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingSingle(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const persistHero = async (imgs) => {
    setC((prev) => ({ ...prev, hero_images: imgs }));
    try { await updateSiteContent({ hero_images: imgs }); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const onHeroUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingHero(true);
    try {
      const added = [];
      for (const f of files) { const res = await uploadHeroImage(f); added.push({ url: res.url, position: "center" }); }
      await persistHero([...(c.hero_images || []), ...added]);
      toast.success(`${added.length} hero image${added.length > 1 ? "s" : ""} added`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingHero(false); if (heroFileRef.current) heroFileRef.current.value = ""; }
  };

  const pickImage = async (url) => {
    const target = pickerFor;
    setPickerFor(null);
    if (target === "about" || target === "contacts") {
      await persistSingleImage(`${target}_image`, url);
      return;
    }
    await persistHero([...(c.hero_images || []), { url, position: "center" }]);
    toast.success("Hero image added");
  };

  const removeHeroAt = (i) => persistHero((c.hero_images || []).filter((_, idx) => idx !== i));
  const setHeroPosAt = (i, pos) => persistHero((c.hero_images || []).map((h, idx) => (idx === i ? { ...h, position: pos } : h)));
  const moveHero = (from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [...(c.hero_images || [])];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    persistHero(arr);
  };

  const setHeroInterval = (v) => {
    const n = Math.max(1, Math.min(60, parseFloat(v) || 1));
    setC((prev) => ({ ...prev, hero_interval: n }));
  };
  const saveHeroInterval = async () => {
    try { await updateSiteContent({ hero_interval: c.hero_interval || 5 }); toast.success("Slideshow timing saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSiteContent({
        hero_line1: c.hero_line1, hero_line2: c.hero_line2, hero_tagline: c.hero_tagline, about_intro: c.about_intro,
        hero_line1_pt: c.hero_line1_pt, hero_line2_pt: c.hero_line2_pt, hero_tagline_pt: c.hero_tagline_pt, about_intro_pt: c.about_intro_pt,
        footer_left: c.footer_left || "", footer_left_pt: c.footer_left_pt || "",
        contacts_text: c.contacts_text || "", contacts_text_pt: c.contacts_text_pt || "",
        about_title: c.about_title || "", about_title_pt: c.about_title_pt || "",
        contacts_title: c.contacts_title || "", contacts_title_pt: c.contacts_title_pt || "",
        contacts_intro: c.contacts_intro || "", contacts_intro_pt: c.contacts_intro_pt || "",
        about_caption: c.about_caption || "", about_caption_pt: c.about_caption_pt || "",
        contacts_caption: c.contacts_caption || "", contacts_caption_pt: c.contacts_caption_pt || "",
        footer_copyright: c.footer_copyright || "", footer_copyright_pt: c.footer_copyright_pt || "",
        hero_images: c.hero_images || [], hero_interval: c.hero_interval || 5,
        background_color: c.background_color || "", text_color: c.text_color || "",
      });
      toast.success("Site text saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onBgChange = (raw) => {
    const cleaned = raw.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    const hex = cleaned ? `#${cleaned}` : "";
    setC((prev) => ({ ...prev, background_color: hex }));
    if (/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(hex)) {
      document.documentElement.style.setProperty("--vsl-bg", hex);
    }
  };

  const onTextColorChange = (raw) => {
    const cleaned = raw.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    const hex = cleaned ? `#${cleaned}` : "";
    setC((prev) => ({ ...prev, text_color: hex }));
    if (/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(hex)) {
      document.documentElement.style.setProperty("--vsl-fg", hex);
    }
  };

  const allImages = projects.flatMap((p) => (p.images || [])
    .filter((im) => im.type !== "video")
    .map((im) => ({ ...im, project: p.name })));

  return (
    <div data-testid="site-text-panel" className="max-w-3xl space-y-10">
      {/* Homepage hero — one or more images (cross-fade slideshow) */}
      <section data-testid="hero-image-section" className="space-y-4 border border-[var(--vsl-border)] p-5">
        <div>
          <h3 className="font-display text-3xl mb-1">Homepage Hero</h3>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)]">Add one or more images for the top of the homepage. With several images they cross-fade as a slideshow; with a single image it stays static. Leave empty to use the first project's cover.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--vsl-muted)]">Seconds per image</label>
          <input data-testid="hero-interval-input" type="number" min={1} max={60} step={0.5}
            value={c.hero_interval ?? 5} onChange={(e) => setHeroInterval(e.target.value)} onBlur={saveHeroInterval}
            className="w-24 bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
          <span className="font-mono text-[10px] text-[var(--vsl-muted)]">
            {heroImages.length > 1 ? "each image fades to the next" : "applies once you add a second image"}
          </span>
        </div>

        {heroImages.length > 0 ? (
          <div data-testid="hero-images-list" className="space-y-3">
            {heroImages.map((h, i) => (
              <div key={h.url} data-testid={`hero-image-row-${i}`} draggable
                onDragStart={() => { heroDrag.current = i; setHeroDragIdx(i); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { moveHero(heroDrag.current, i); heroDrag.current = null; setHeroDragIdx(null); }}
                onDragEnd={() => { heroDrag.current = null; setHeroDragIdx(null); }}
                className={`flex items-start gap-4 border border-[var(--vsl-border)] p-3 bg-[#141312] ${heroDragIdx === i ? "opacity-40" : ""}`}>
                <GripVertical size={16} className="text-[var(--vsl-muted)] mt-1 cursor-move shrink-0" data-testid={`hero-grip-${i}`} />
                <div className="w-[200px] shrink-0">
                  <div className="aspect-video overflow-hidden rounded border border-[var(--vsl-border)] bg-[#0d0d0c]">
                    <img src={mediaUrl(h.url)} alt="" style={{ objectPosition: h.position || "center" }} className="w-full h-full object-cover" />
                  </div>
                  <p className="font-mono text-[9px] text-[var(--vsl-muted)] mt-1">Image {i + 1}</p>
                </div>
                <div className="shrink-0">
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[var(--vsl-muted)] mb-1.5">Anchor</p>
                  <div className="grid grid-cols-3 gap-1 w-[78px]">
                    {HERO_POSITIONS.map((pos) => (
                      <button key={pos} type="button" onClick={() => setHeroPosAt(i, pos)} title={pos}
                        data-testid={`hero-pos-${i}-${pos.replace(" ", "-")}`}
                        className={`aspect-square border transition-colors ${(h.position || "center") === pos ? "bg-[var(--vsl-limestone)] border-[var(--vsl-limestone)]" : "border-[var(--vsl-border)] hover:border-[var(--vsl-fg)]"}`} />
                    ))}
                  </div>
                </div>
                <button onClick={() => removeHeroAt(i)} data-testid={`hero-remove-${i}`}
                  className="ml-auto text-[var(--vsl-muted)] hover:text-red-400 transition-colors shrink-0" title="Remove"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full max-w-[320px] aspect-video bg-[#141312] overflow-hidden border border-[var(--vsl-border)] flex items-center justify-center font-mono text-[10px] text-[var(--vsl-muted)] uppercase tracking-wider">
            Using first project cover
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button onClick={() => heroFileRef.current?.click()} disabled={uploadingHero} data-testid="hero-upload-btn"
            className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors disabled:opacity-50">
            <Upload size={13} /> {uploadingHero ? "Uploading…" : "Add image"}
          </button>
          <input ref={heroFileRef} type="file" accept="image/*" multiple hidden onChange={onHeroUpload} data-testid="hero-file-input" />
          <button onClick={() => setPickerFor("hero")} data-testid="hero-pick-btn"
            className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors">
            <Images size={13} /> Add from projects
          </button>
        </div>
      </section>

      <section data-testid="bg-color-section" className="space-y-3 border border-[var(--vsl-border)] p-5">
        <div>
          <h3 className="font-display text-3xl mb-1">Website Colours</h3>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)]">Set the background and text colours of the entire website. Enter 6-digit hex values. Text colour applies to text over the plain background (not text over images).</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--vsl-muted)]">Website background&nbsp;#</label>
          <input data-testid="bg-color-input" value={(c.background_color || "").replace(/^#/, "")} onChange={(e) => onBgChange(e.target.value)}
            placeholder="0A0A09" maxLength={6} spellCheck="false"
            className="w-32 bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] uppercase tracking-wider" />
          <span data-testid="bg-color-swatch" className="w-9 h-9 rounded border border-[var(--vsl-border)] shrink-0"
            style={{ backgroundColor: /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(c.background_color || "") ? c.background_color : "#0A0A09" }} />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--vsl-muted)]">Website text&nbsp;#</label>
          <input data-testid="text-color-input" value={(c.text_color || "").replace(/^#/, "")} onChange={(e) => onTextColorChange(e.target.value)}
            placeholder="F4F0EA" maxLength={6} spellCheck="false"
            className="w-32 bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] uppercase tracking-wider" />
          <span data-testid="text-color-swatch" className="w-9 h-9 rounded border border-[var(--vsl-border)] shrink-0"
            style={{ backgroundColor: /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(c.text_color || "") ? c.text_color : "#F4F0EA" }} />
        </div>
      </section>

      <div className="flex items-center gap-2 sticky top-[104px] z-10 bg-[var(--vsl-bg)]/90 backdrop-blur py-2">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mr-2">Editing:</span>
        {["en", "pt"].map((l) => (
          <button key={l} data-testid={`sitetext-lang-${l}`} onClick={() => setEditLang(l)}
            className={`font-mono text-[11px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors ${editLang === l ? "border-[var(--vsl-fg)] text-[var(--vsl-fg)]" : "border-[var(--vsl-border)] text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)]"}`}>
            {l === "en" ? "English" : "Português"}
          </button>
        ))}
      </div>

      <section className="space-y-5">
        <h3 className="font-display text-3xl">Hero</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Heading line 1" value={g("hero_line1")} onChange={(v) => set("hero_line1", v)} testid="hero-line1" />
          <Field label="Heading line 2" value={g("hero_line2")} onChange={(v) => set("hero_line2", v)} testid="hero-line2" />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Tagline</label>
          <RichTextEditor value={g("hero_tagline")} onChange={(v) => set("hero_tagline", v)} placeholder="Tagline" testid="hero-tagline" />
        </div>
      </section>

      <section className="space-y-5">
        <h3 className="font-display text-3xl">About</h3>
        <Field label="Heading (one line per row)" value={g("about_title")} onChange={(v) => set("about_title", v)} textarea testid="about-title" />
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Text</label>
          <RichTextEditor value={g("about_intro")} onChange={(v) => set("about_intro", v)} placeholder="About text" testid="about-intro" />
        </div>
        <div data-testid="about-image-section" className="space-y-3 border border-[var(--vsl-border)] p-5">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mb-1">Image</p>
            <p className="font-mono text-[11px] text-[var(--vsl-muted)]">Shown with the About text: a portrait image sits to the left of the text on desktop, a landscape one below it. On mobile it always sits below the text. Never cropped. Remove it to show no image at all.</p>
          </div>
          <ImageBlock prefix="about" url={c.about_image} uploading={uploadingSingle === "about_image"} fileRef={aboutFileRef}
            onUpload={(e) => onSingleUpload("about_image", e, aboutFileRef)} onPick={() => setPickerFor("about")}
            onClear={() => persistSingleImage("about_image", "")} />
          <div>
            <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Caption</label>
            <RichTextEditor value={g("about_caption")} onChange={(v) => set("about_caption", v)} placeholder="Caption (optional)" testid="about-caption" />
          </div>
        </div>
      </section>

      <section className="space-y-5">
        <h3 className="font-display text-3xl">Contacts</h3>
        <Field label="Heading (one line per row)" value={g("contacts_title")} onChange={(v) => set("contacts_title", v)} textarea testid="contacts-title" />
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Text</label>
          <RichTextEditor value={g("contacts_intro")} onChange={(v) => set("contacts_intro", v)} placeholder="Text" testid="contacts-intro-editor" />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Contact details</label>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)] mb-2">Shown with the Contacts text. Supports formatting, links and colours. Leave empty to show nothing.</p>
          <RichTextEditor value={g("contacts_text")} onChange={(v) => set("contacts_text", v)} placeholder="Contact details" testid="contacts-text" />
        </div>
        <div data-testid="contacts-image-section" className="space-y-3 border border-[var(--vsl-border)] p-5">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mb-1">Image</p>
            <p className="font-mono text-[11px] text-[var(--vsl-muted)]">Shown with the Contacts text: a portrait image sits to the left of the text on desktop, a landscape one below it. On mobile it always sits below the text. Never cropped. Remove it to show no image at all.</p>
          </div>
          <ImageBlock prefix="contacts" url={c.contacts_image} uploading={uploadingSingle === "contacts_image"} fileRef={contactsFileRef}
            onUpload={(e) => onSingleUpload("contacts_image", e, contactsFileRef)} onPick={() => setPickerFor("contacts")}
            onClear={() => persistSingleImage("contacts_image", "")} />
          <div>
            <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Caption</label>
            <RichTextEditor value={g("contacts_caption")} onChange={(v) => set("contacts_caption", v)} placeholder="Caption (optional)" testid="contacts-caption" />
          </div>
        </div>
      </section>

      <section className="space-y-5">
        <h3 className="font-display text-3xl">Footer</h3>
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Left text</label>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)] mb-2">Free text shown at the bottom-left of every page, under the logo. Supports formatting, links and colours. Leave empty to show nothing. The site tree links on the right are fixed.</p>
          <RichTextEditor value={g("footer_left")} onChange={(v) => set("footer_left", v)} placeholder="Footer text" testid="footer-left" />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Copyright line</label>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)] mb-2">The small line at the very bottom of every page. Type <code>{"{year}"}</code> anywhere to insert the current year automatically. Leave empty to show nothing.</p>
          <RichTextEditor value={g("footer_copyright")} onChange={(v) => set("footer_copyright", v)} placeholder="© {year} VESSEL — All rights reserved" testid="footer-copyright" />
        </div>
      </section>

      <button data-testid="save-site-text" onClick={save} disabled={saving}
        className="bg-[var(--vsl-fg)] text-[var(--vsl-bg)] px-6 py-3 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[var(--vsl-limestone)] transition-colors disabled:opacity-50">
        <Save size={14} /> {saving ? "Saving…" : "Save all"}
      </button>

      {pickerFor && (
        <div data-testid="hero-picker-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setPickerFor(null)}>
          <div className="bg-[var(--vsl-bg)] border border-[var(--vsl-border)] w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[var(--vsl-border)]">
              <h4 className="font-mono text-xs tracking-[0.2em] uppercase">Pick a{pickerFor === "about" ? "n about" : " hero"} image from projects</h4>
              <button onClick={() => setPickerFor(null)} data-testid="hero-picker-close" className="text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)]"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {allImages.map((im) => (
                <button key={im.id} onClick={() => pickImage(im.url)} data-testid={`hero-pick-${im.id}`}
                  className="group relative h-44 bg-[#141312] overflow-hidden border border-[var(--vsl-border)] hover:border-[var(--vsl-fg)] transition-colors">
                  <img src={mediaUrl(im.url)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[var(--vsl-fg)] font-mono text-[10px] px-2 py-1 truncate">{im.project}</span>
                </button>
              ))}
              {allImages.length === 0 && <p className="col-span-full font-mono text-xs text-[var(--vsl-muted)]">No project images available.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
